<template>
    <div
        class="almighty-mention-editor"
        :class="{
            'is-focused': isFocused,
            'is-disabled': disabled,
        }"
        @click="focusEditor"
    >
        <div
            ref="editorRef"
            class="almighty-mention-editor__content"
            :contenteditable="disabled ? 'false' : 'true'"
            :data-placeholder="placeholder"
            :data-empty="isEmpty ? 'true' : 'false'"
            role="textbox"
            aria-multiline="true"
            @input="handleInput"
            @keydown="handleKeydown"
            @paste="handlePaste"
            @focus="isFocused = true"
            @blur="handleBlur"
            @mouseup="updateMentionState"
            @keyup="handleKeyup"
        ></div>
        <span
            class="almighty-mention-editor__count"
            :class="{
                'is-near-limit': characterCount > maxLength * 0.8,
                'is-at-limit': characterCount >= maxLength,
            }"
        >
            {{ characterCount }}/{{ maxLength }}
        </span>

        <ClientOnly>
            <Teleport to="body">
                <div
                    v-if="shouldShowMentionMenu"
                    ref="menuRef"
                    class="almighty-mention-menu fixed z-[9999]"
                    :style="mentionMenuStyle"
                    @mousedown.prevent
                >
                    <button
                        v-for="(asset, index) in filteredAssets"
                        :key="asset.id"
                        type="button"
                        class="almighty-mention-menu__item"
                        :class="{ 'is-active': index === activeIndex }"
                        @mousedown.prevent="insertMention(asset)"
                    >
                        <span class="almighty-mention-menu__thumb">
                            <img
                                v-if="asset.previewUrl && asset.kind === 'image'"
                                :src="asset.previewUrl"
                                :alt="asset.label"
                                class="almighty-mention-menu__preview"
                            />
                            <video
                                v-else-if="asset.previewUrl && asset.kind === 'video'"
                                :src="asset.previewUrl"
                                class="almighty-mention-menu__preview"
                                muted
                                playsinline
                                preload="metadata"
                            ></video>
                            <span v-else class="almighty-mention-menu__audio">
                                A
                            </span>
                        </span>
                        <span class="almighty-mention-menu__label">
                            {{ asset.label }}
                        </span>
                    </button>
                </div>
            </Teleport>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type {
    AlmightyMentionAsset,
    AlmightyPromptMentionToken,
} from '~/utils/almightyMention'
import {
    cloneAlmightyPromptMentionTokens,
    normalizeAlmightyPromptMentionTokens,
    serializeAlmightyPromptMentionTokens,
} from '~/utils/almightyMention'

const props = withDefaults(
    defineProps<{
        assets?: AlmightyMentionAsset[]
        maxLength?: number
        placeholder?: string
        disabled?: boolean
    }>(),
    {
        assets: () => [],
        maxLength: 1000,
        placeholder: '',
        disabled: false,
    }
)

const prompt = defineModel<string>('prompt', {
    default: '',
})

const tokens = defineModel<AlmightyPromptMentionToken[]>('tokens', {
    default: () => [],
})

const editorRef = ref<HTMLDivElement | null>(null)
const menuRef = ref<HTMLDivElement | null>(null)
const isFocused = ref(false)
const activeIndex = ref(0)
const mentionState = ref<{
    start: number
    end: number
    query: string
} | null>(null)
const menuPosition = ref({ top: 0, left: 0 })
const rendering = ref(false)

const normalizedTokens = computed(() =>
    normalizeAlmightyPromptMentionTokens(tokens.value, prompt.value)
)

const assetMap = computed(() => {
    return new Map((props.assets || []).map(asset => [asset.id, asset]))
})

const getCharWeight = (char: string) => {
    const codePoint = char.codePointAt(0) || 0
    if (codePoint <= 0x00ff) return 1
    if (codePoint >= 0xff61 && codePoint <= 0xff9f) return 1
    return 2
}

const getWeightedCharLength = (value: string) => {
    let total = 0
    for (const char of value) {
        total += getCharWeight(char)
    }
    return total
}

const sliceByWeightedLength = (value: string, maxLength: number) => {
    if (maxLength <= 0) return ''
    let consumed = 0
    let result = ''
    for (const char of value) {
        const weight = getCharWeight(char)
        if (consumed + weight > maxLength) break
        consumed += weight
        result += char
    }
    return result
}

const characterCount = computed(() => {
    return getWeightedCharLength(
        serializeAlmightyPromptMentionTokens(
            normalizedTokens.value,
            props.assets
        )
    )
})

const isEmpty = computed(() => {
    return normalizedTokens.value.every(token => {
        if (token.type === 'mention') return false
        return !token.text.trim()
    })
})

const filteredAssets = computed(() => {
    if (!mentionState.value) return []
    const query = mentionState.value.query.trim().toLowerCase()
    if (!query) return props.assets || []
    return (props.assets || []).filter(asset =>
        asset.label.toLowerCase().includes(query)
    )
})

const shouldShowMentionMenu = computed(() => {
    return (
        !props.disabled &&
        !!mentionState.value &&
        filteredAssets.value.length > 0
    )
})

const mentionMenuStyle = computed(() => ({
    top: `${menuPosition.value.top}px`,
    left: `${menuPosition.value.left}px`,
}))

const focusEditor = () => {
    if (props.disabled) return
    nextTick(() => {
        editorRef.value?.focus()
    })
}

const getTokenDisplayText = (token: AlmightyPromptMentionToken) => {
    if (token.type === 'text') return token.text
    return assetMap.value.get(token.assetId)?.label || token.fallbackLabel
}

const truncateTokensToMaxLength = (
    sourceTokens: AlmightyPromptMentionToken[],
    maxLength: number
) => {
    const nextTokens: AlmightyPromptMentionToken[] = []
    let remaining = Math.max(0, maxLength)

    for (const token of sourceTokens) {
        if (remaining <= 0) break

        if (token.type === 'text') {
            const text = sliceByWeightedLength(token.text, remaining)
            if (text) {
                nextTokens.push({ type: 'text', text })
                remaining -= getWeightedCharLength(text)
            }
            continue
        }

        const label = getTokenDisplayText(token)
        const tokenLength = getWeightedCharLength(label)
        if (tokenLength > remaining) break
        nextTokens.push({ ...token })
        remaining -= tokenLength
    }

    return cloneAlmightyPromptMentionTokens(nextTokens)
}

const createMentionChip = (
    token: Extract<AlmightyPromptMentionToken, { type: 'mention' }>
) => {
    const asset = assetMap.value.get(token.assetId)
    const label = asset?.label || token.fallbackLabel
    const chip = document.createElement('span')
    chip.contentEditable = 'false'
    chip.className = 'almighty-mention-chip'
    chip.dataset.assetId = token.assetId
    chip.dataset.kind = token.kind
    chip.dataset.fallbackLabel = token.fallbackLabel
    chip.dataset.displayText = label

    const thumb = document.createElement('span')
    thumb.className = 'almighty-mention-chip__thumb'
    if (asset?.previewUrl && asset.kind === 'image') {
        const img = document.createElement('img')
        img.src = asset.previewUrl
        img.alt = label
        img.className = 'almighty-mention-chip__preview'
        thumb.append(img)
    } else if (asset?.previewUrl && asset.kind === 'video') {
        const video = document.createElement('video')
        video.src = asset.previewUrl
        video.muted = true
        video.playsInline = true
        video.preload = 'metadata'
        video.className = 'almighty-mention-chip__preview'
        thumb.append(video)
    } else {
        const audio = document.createElement('span')
        audio.className = 'almighty-mention-chip__audio'
        audio.textContent = 'A'
        thumb.append(audio)
    }

    const text = document.createElement('span')
    text.className = 'almighty-mention-chip__label'
    text.textContent = label

    chip.append(thumb, text)
    return chip
}

const renderTokens = () => {
    const editor = editorRef.value
    if (!editor) return
    rendering.value = true
    editor.innerHTML = ''
    for (const token of normalizedTokens.value) {
        if (token.type === 'text') {
            editor.append(document.createTextNode(token.text))
        } else {
            editor.append(createMentionChip(token))
        }
    }
    editor.dataset.empty = isEmpty.value ? 'true' : 'false'
    rendering.value = false
}

const setCaretAtEditorEnd = () => {
    const editor = editorRef.value
    if (!editor) return
    const selection = window.getSelection()
    if (!selection) return
    const range = document.createRange()
    range.selectNodeContents(editor)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
}

const parseNodeTokens = (node: Node): AlmightyPromptMentionToken[] => {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent || '').replace(/\u00a0/g, ' ')
        return text ? [{ type: 'text', text }] : []
    }
    if (!(node instanceof HTMLElement)) return []
    if (node.dataset.assetId) {
        return [
            {
                type: 'mention',
                assetId: node.dataset.assetId,
                kind: (node.dataset.kind || 'image') as any,
                fallbackLabel:
                    node.dataset.fallbackLabel ||
                    node.dataset.displayText ||
                    '',
            },
        ]
    }
    if (node.tagName === 'BR') {
        return [{ type: 'text', text: '\n' }]
    }
    return Array.from(node.childNodes).flatMap(parseNodeTokens)
}

const parseEditorTokens = () => {
    const editor = editorRef.value
    if (!editor) return []
    return cloneAlmightyPromptMentionTokens(
        Array.from(editor.childNodes).flatMap(parseNodeTokens)
    )
}

const syncModelsFromEditor = () => {
    if (rendering.value) return
    let nextTokens = parseEditorTokens()
    let nextPrompt = serializeAlmightyPromptMentionTokens(
        nextTokens,
        props.assets
    )
    const shouldTruncate = getWeightedCharLength(nextPrompt) > props.maxLength

    if (shouldTruncate) {
        nextTokens = truncateTokensToMaxLength(nextTokens, props.maxLength)
        nextPrompt = serializeAlmightyPromptMentionTokens(
            nextTokens,
            props.assets
        )
    }

    tokens.value = nextTokens
    prompt.value = nextPrompt
    editorRef.value?.setAttribute(
        'data-empty',
        nextTokens.length ? 'false' : 'true'
    )

    if (shouldTruncate) {
        nextTick(() => {
            renderTokens()
            setCaretAtEditorEnd()
            updateMentionState()
        })
    }
}

const getRangeInEditor = () => {
    const editor = editorRef.value
    const selection = window.getSelection()
    if (!editor || !selection || selection.rangeCount === 0) return null
    const range = selection.getRangeAt(0)
    if (!editor.contains(range.startContainer)) return null
    return range
}

const getNodeDisplayLength = (node: Node): number => {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(/\u00a0/g, ' ').length
    }
    if (!(node instanceof HTMLElement)) return 0
    if (node.dataset.displayText) return node.dataset.displayText.length
    if (node.tagName === 'BR') return 1
    return Array.from(node.childNodes).reduce(
        (total, child) => total + getNodeDisplayLength(child),
        0
    )
}

const getNodeWeightedLength = (node: Node): number => {
    if (node.nodeType === Node.TEXT_NODE) {
        return getWeightedCharLength(
            (node.textContent || '').replace(/\u00a0/g, ' ')
        )
    }
    if (!(node instanceof HTMLElement)) return 0
    if (node.dataset.displayText) {
        return getWeightedCharLength(node.dataset.displayText)
    }
    if (node.tagName === 'BR') return 1
    return Array.from(node.childNodes).reduce(
        (total, child) => total + getNodeWeightedLength(child),
        0
    )
}

const getCaretOffset = () => {
    const editor = editorRef.value
    const range = getRangeInEditor()
    if (!editor || !range) return 0
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(editor)
    preCaretRange.setEnd(range.startContainer, range.startOffset)
    return Array.from(preCaretRange.cloneContents().childNodes).reduce(
        (total, child) => total + getNodeDisplayLength(child),
        0
    )
}

const getSelectionContentLength = () => {
    const range = getRangeInEditor()
    if (!range || range.collapsed) return 0
    return Array.from(range.cloneContents().childNodes).reduce(
        (total, child) => total + getNodeWeightedLength(child),
        0
    )
}

const resolveMentionContext = (value: string, caretPosition: number) => {
    const beforeCaret = value.slice(0, caretPosition)
    const start = beforeCaret.lastIndexOf('@')
    if (start < 0) return null
    const query = beforeCaret.slice(start + 1)
    if (/\s/.test(query)) return null
    return {
        start,
        end: caretPosition,
        query,
    }
}

const updateMenuPosition = () => {
    const editor = editorRef.value
    const range = getRangeInEditor()
    if (!editor) return
    const rect = range?.getBoundingClientRect()
    const editorRect = editor.getBoundingClientRect()
    const top = (rect?.bottom || editorRect.bottom) + 8
    const left = Math.min(
        rect?.left || editorRect.left,
        window.innerWidth - 252
    )
    menuPosition.value = {
        top,
        left: Math.max(12, left),
    }
}

const updateMentionState = () => {
    if (!props.assets?.length || props.disabled) {
        mentionState.value = null
        return
    }
    const text = serializeAlmightyPromptMentionTokens(
        parseEditorTokens(),
        props.assets
    )
    const context = resolveMentionContext(text, getCaretOffset())
    mentionState.value = context
    activeIndex.value = 0
    if (context) nextTick(updateMenuPosition)
}

const createRangeFromOffsets = (start: number, end: number) => {
    const editor = editorRef.value
    const range = document.createRange()
    if (!editor) return range

    let offset = 0
    let startSet = false
    let endSet = false

    const walk = (node: Node) => {
        if (endSet) return
        const length = getNodeDisplayLength(node)

        if (node.nodeType === Node.TEXT_NODE) {
            const nextOffset = offset + length
            if (!startSet && start <= nextOffset) {
                range.setStart(node, Math.max(0, start - offset))
                startSet = true
            }
            if (startSet && end <= nextOffset) {
                range.setEnd(node, Math.max(0, end - offset))
                endSet = true
            }
            offset = nextOffset
            return
        }

        if (node instanceof HTMLElement && node.dataset.displayText) {
            const nextOffset = offset + length
            if (!startSet && start <= nextOffset) {
                range.setStartBefore(node)
                startSet = true
            }
            if (startSet && end <= nextOffset) {
                range.setEndAfter(node)
                endSet = true
            }
            offset = nextOffset
            return
        }

        Array.from(node.childNodes).forEach(walk)
    }

    Array.from(editor.childNodes).forEach(walk)
    if (!startSet) {
        range.selectNodeContents(editor)
        range.collapse(false)
    }
    if (!endSet) {
        range.collapse(false)
    }
    return range
}

const setCaretAtOffset = (offset: number) => {
    const selection = window.getSelection()
    if (!selection) return
    const range = createRangeFromOffsets(offset, offset)
    selection.removeAllRanges()
    selection.addRange(range)
}

const deleteMentionAroundCaret = (direction: 'backward' | 'forward') => {
    const range = getRangeInEditor()
    if (!range || !range.collapsed) return false

    const nextTokens = parseEditorTokens()
    const caretOffset = getCaretOffset()
    let offset = 0
    let deleteIndex = -1
    let nextCaretOffset = caretOffset

    for (let index = 0; index < nextTokens.length; index += 1) {
        const token = nextTokens[index]
        if (!token) continue
        const length =
            token.type === 'mention'
                ? getTokenDisplayText(token).length
                : token.text.length
        const start = offset
        const end = offset + length

        if (
            token.type === 'mention' &&
            ((direction === 'backward' && caretOffset === end) ||
                (direction === 'forward' && caretOffset === start))
        ) {
            deleteIndex = index
            nextCaretOffset = start
            break
        }

        offset = end
    }

    if (deleteIndex < 0) return false

    nextTokens.splice(deleteIndex, 1)
    tokens.value = cloneAlmightyPromptMentionTokens(nextTokens)
    prompt.value = serializeAlmightyPromptMentionTokens(nextTokens, props.assets)
    mentionState.value = null

    nextTick(() => {
        renderTokens()
        setCaretAtOffset(nextCaretOffset)
        updateMentionState()
    })

    return true
}

const insertMention = (asset: AlmightyMentionAsset) => {
    const editor = editorRef.value
    const state = mentionState.value
    if (!editor || !state) return

    const range = createRangeFromOffsets(state.start, state.end)
    range.deleteContents()

    const chip = createMentionChip({
        type: 'mention',
        assetId: asset.id,
        kind: asset.kind,
        fallbackLabel: asset.label,
    })
    const space = document.createTextNode(' ')
    range.insertNode(space)
    range.insertNode(chip)

    const selection = window.getSelection()
    const nextRange = document.createRange()
    nextRange.setStartAfter(space)
    nextRange.collapse(true)
    selection?.removeAllRanges()
    selection?.addRange(nextRange)

    mentionState.value = null
    syncModelsFromEditor()
    nextTick(() => {
        editor.focus()
    })
}

const handleInput = () => {
    syncModelsFromEditor()
    updateMentionState()
}

const handlePaste = (event: ClipboardEvent) => {
    event.preventDefault()
    const text = event.clipboardData?.getData('text/plain') || ''
    if (!text) return
    const remaining =
        props.maxLength - characterCount.value + getSelectionContentLength()
    const safeText = sliceByWeightedLength(text, remaining)
    if (!safeText) return
    document.execCommand('insertText', false, safeText)
    syncModelsFromEditor()
    updateMentionState()
}

const handleKeydown = (event: KeyboardEvent) => {
    if (props.disabled) return
    if (
        (event.key === 'Backspace' &&
            deleteMentionAroundCaret('backward')) ||
        (event.key === 'Delete' && deleteMentionAroundCaret('forward'))
    ) {
        event.preventDefault()
        return
    }

    const isPlainTextInput =
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
    const incomingLength =
        event.key === 'Enter' ? 1 : getWeightedCharLength(event.key)

    if (
        (isPlainTextInput || event.key === 'Enter') &&
        characterCount.value - getSelectionContentLength() + incomingLength >
            props.maxLength
    ) {
        event.preventDefault()
        return
    }

    if (shouldShowMentionMenu.value) {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            activeIndex.value = Math.min(
                activeIndex.value + 1,
                filteredAssets.value.length - 1
            )
            return
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            activeIndex.value = Math.max(activeIndex.value - 1, 0)
            return
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            const asset = filteredAssets.value[activeIndex.value]
            if (asset) insertMention(asset)
            return
        }
        if (event.key === 'Escape') {
            event.preventDefault()
            mentionState.value = null
            return
        }
    }
    if (event.key === 'Enter') {
        event.preventDefault()
        document.execCommand('insertText', false, '\n')
        syncModelsFromEditor()
        updateMentionState()
        return
    }
    nextTick(updateMentionState)
}

const handleKeyup = (event: KeyboardEvent) => {
    if (
        shouldShowMentionMenu.value &&
        ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(event.key)
    ) {
        return
    }
    updateMentionState()
}

const handleBlur = () => {
    isFocused.value = false
    window.setTimeout(() => {
        if (menuRef.value?.matches(':hover')) return
        mentionState.value = null
    }, 120)
}

const handleOutsidePointerDown = (event: PointerEvent) => {
    const target = event.target
    if (!(target instanceof Node)) return
    if (editorRef.value?.contains(target)) return
    if (menuRef.value?.contains(target)) return
    mentionState.value = null
}

watch(
    () => [prompt.value, tokens.value, props.assets],
    () => {
        if (isFocused.value) return
        nextTick(renderTokens)
    },
    {
        deep: true,
    }
)

watch(
    () => props.assets,
    () => {
        nextTick(renderTokens)
    },
    {
        deep: true,
    }
)

onMounted(() => {
    renderTokens()
    document.addEventListener('pointerdown', handleOutsidePointerDown, true)
})

onUnmounted(() => {
    document.removeEventListener('pointerdown', handleOutsidePointerDown, true)
})
</script>

<style scoped lang="less">
.almighty-mention-editor {
    position: relative;
    width: 100%;

    &.is-disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
}

.almighty-mention-editor__content {
    position: relative;
    display: block;
    width: 100%;
    min-height: 96px;
    max-height: 220px;
    overflow-y: auto;
    padding-right: 12px;
    padding-bottom: 28px;
    color: #fff;
    font-size: 14px;
    line-height: 24px;
    white-space: pre-wrap;
    word-break: break-word;
    outline: none;
    cursor: text;

    &[data-empty='true']::before {
        content: attr(data-placeholder);
        position: absolute;
        top: 0;
        left: 0;
        right: 12px;
        color: rgba(255, 255, 255, 0.35);
        white-space: pre-wrap;
        pointer-events: none;
    }
}

.almighty-mention-editor__count {
    position: absolute;
    right: 10px;
    bottom: 8px;
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    line-height: 1;
    pointer-events: none;

    &.is-near-limit {
        color: #facc15;
    }

    &.is-at-limit {
        color: #f87171;
    }
}

:deep(.almighty-mention-chip) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0 2px;
    padding: 2px 8px 2px 4px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    vertical-align: middle;
}

:deep(.almighty-mention-chip__thumb) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.12);
    flex-shrink: 0;
}

:deep(.almighty-mention-chip__preview) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

:deep(.almighty-mention-chip__audio) {
    color: rgba(255, 255, 255, 0.9);
    font-size: 10px;
    line-height: 1;
}

:deep(.almighty-mention-chip__label) {
    color: rgba(255, 255, 255, 0.92);
    font-size: 12px;
    line-height: 16px;
}

.almighty-mention-menu {
    width: 240px;
    max-height: min(320px, 45vh);
    overflow-y: auto;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    background: rgba(10, 10, 12, 0.92);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(20px);
}

.almighty-mention-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    text-align: left;
    transition: background-color 0.2s ease;

    &:hover,
    &.is-active {
        background: rgba(255, 255, 255, 0.12);
    }
}

.almighty-mention-menu__thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    overflow: hidden;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.08);
}

.almighty-mention-menu__preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.almighty-mention-menu__audio,
.almighty-mention-menu__label {
    color: rgba(255, 255, 255, 0.92);
    font-size: 14px;
    line-height: 20px;
}
</style>
